import * as fromRouter from '@ngrx/router-store';
import { ActionReducerMap, combineReducers } from '@ngrx/store';
import { PlansReducer, State as PlanState } from './internal/plans';
import { SetupReducer, State as SetupState } from './internal/setup';
import { PropertyReducer, State as PropertyState } from './internal/property';
import { InternalReducer, InternalState } from './internal/shared';
import { PhotoReducer, State as PhotoState } from './internal/photos';
import { FilesReducer, State as FilesState } from './internal/files';
import { LocationState } from './internal/component/location-store/locations.state';
import { AuthReducer } from './shared/auth/redux';
import { authActionTypes } from './shared/auth/redux/auth.actions';
import { AuthState } from './shared/auth/redux/auth.state';
import { ReportState } from './internal/reports/redux/reports.state';
import { ReportReducer } from './internal/reports/redux';
import { GlobalState } from './shared/redux/global/global.state';
import { GlobalReducer } from './shared/redux/global';
import { UserState } from './internal/user/redux/user.state';
import { UserReducer } from './internal/user/redux';
import { ComponentState } from './internal/component/component-store/component.state';
import { ComponentReducer } from './internal/component/component-store';
import { LocationsReducer } from './internal/component/location-store';
import { PublicReducer, State as PublicState } from './public/redux';
import { FilterSettingsState } from './internal/shared/filter-settings-store/filter-settings.state';
import { FilterSettingsReducer } from './internal/shared/filter-settings-store';
import { WorkspaceState } from './internal/workspace/workspace-store/workspace.state';
import { WorkspaceReducer } from './internal/workspace/workspace-store';
import { ActivityState } from './internal/activity/activity-store/activity.state';
import { ActivityReducer } from './internal/activity/activity-store';
import { AdminReducer } from './internal/admin/admin-store';
import { AdminState } from './internal/admin/admin-store/admin.state';
import { MappingState } from './internal/mapping/mapping-store/mapping.state';
import { MappingReducer } from './internal/mapping/mapping-store';
import * as fromCategory from './internal/component/category-store/reducers/categories.reducer';
import * as fromTags from './internal/tags/tags-store/reducers/tagging.reducer';
import * as fromConnectedApps from './internal/connected-apps/store/connected-apps.reducer';
import * as fromTransactions from './internal/transactions/transactions-store/transactions.reducer';
import * as fromPortfolioAnalytics from './internal/portfolio-analytics/store/reducers/portfolio-analytics.reducer';
import * as fromMaintenance from './internal/maintenance/maintenance-store/reducers/maintenance.reducer';
import * as fromWorkspaceGroups from './internal/portfolio-analytics/store/reducers/workspace-group.reducer';
import * as fromProjects from './internal/projects/projects-store/projects.reducer';
import * as fromAssetLibrary from './internal/asset-library/store/reducers/asset-library.reducer';
import * as fromNotifications from './internal/notifications/store/reducers/notifications.reducer';
import * as fromUserSettings from './internal/user-settings/store/reducers/user-settings.reducer';
import * as fromAccounts from './internal/accounts/store/accounts.reducer';
import * as fromReserves from './internal/reserves/store/reserves.reducer';
import * as fromReservesV2 from './internal/reserves-v2/store/reserves-v2.reducer';
import * as fromMembership from './internal/membership/store/membership.reducer';

export interface AppState {
  router: fromRouter.RouterReducerState;
  property: PropertyState;
  internal: InternalState;
  auth: AuthState;
  component: ComponentState;
  reports: ReportState;
  global: GlobalState;
  user: UserState;
  plans: PlanState;
  workspace: WorkspaceState;
  activity: ActivityState;
  mapping: MappingState;
  setup: SetupState,
  locations: LocationState;
  public: PublicState;
  filterSettings: FilterSettingsState;
  photo: PhotoState;
  files: FilesState;
  [fromProjects.projectsFeatureKey]: fromProjects.ProjectState;
  admin: AdminState;
  [fromTransactions.transactionFeatureKey]: fromTransactions.TransactionsState;
  [fromConnectedApps.connectedAppsFeatureKey]: fromConnectedApps.ConnectedAppsState;
  [fromCategory.categoryFeatureKey]: fromCategory.CategoryState;
  [fromTags.tagFeatureKey]: fromTags.TagState,
  [fromPortfolioAnalytics.portfolioAnalyticsFeatureKey]: fromPortfolioAnalytics.PortfolioAnalyticsState,
  [fromMaintenance.maintenanceFeatureKey]: fromMaintenance.MaintenanceState,
  [fromWorkspaceGroups.workspaceGroupsFeatureKey]: fromWorkspaceGroups.WorkspaceGroupState,
  [fromAssetLibrary.assetLibraryFeatureKey]: fromAssetLibrary.AssetLibraryState,
  [fromNotifications.notificationsFeatureKey]: fromNotifications.NotificationState,
  [fromUserSettings.userSettingsFeatureKey]: fromUserSettings.UserSettingsState,
  [fromAccounts.accountsFeatureKey]: fromAccounts.AccountsState,
  [fromReserves.reservesFeatureKey]: fromReserves.ReservesState;
  [fromReservesV2.reservesV2FeatureKey]: fromReservesV2.ReservesV2State;
  [fromMembership.membershipFeatureKey]: fromMembership.MembershipState;
}

export const reducers: ActionReducerMap<AppState> = {
  router: fromRouter.routerReducer,
  property: PropertyReducer,
  internal: InternalReducer,
  auth: AuthReducer,
  component: ComponentReducer,
  reports: ReportReducer,
  global: GlobalReducer,
  user: UserReducer,
  plans: PlansReducer,
  workspace: WorkspaceReducer,
  activity: ActivityReducer,
  mapping: MappingReducer,
  setup: SetupReducer,
  locations: LocationsReducer,
  public: PublicReducer,
  filterSettings: FilterSettingsReducer,
  photo: PhotoReducer,
  files: FilesReducer,
  [fromProjects.projectsFeatureKey]: fromProjects.reducer,
  admin: AdminReducer,
  [fromTransactions.transactionFeatureKey]: fromTransactions.reducer,
  [fromConnectedApps.connectedAppsFeatureKey]: fromConnectedApps.reducer,
  [fromCategory.categoryFeatureKey]: fromCategory.reducer,
  [fromTags.tagFeatureKey]: fromTags.reducer,
  [fromPortfolioAnalytics.portfolioAnalyticsFeatureKey]: fromPortfolioAnalytics.reducer,
  [fromMaintenance.maintenanceFeatureKey]: fromMaintenance.reducer,
  [fromWorkspaceGroups.workspaceGroupsFeatureKey]: fromWorkspaceGroups.reducer,
  [fromAssetLibrary.assetLibraryFeatureKey]: fromAssetLibrary.reducer,
  [fromNotifications.notificationsFeatureKey]: fromNotifications.reducer,
  [fromUserSettings.userSettingsFeatureKey]: fromUserSettings.reducer,
  [fromAccounts.accountsFeatureKey]: fromAccounts.reducer,
  [fromReserves.reservesFeatureKey]: fromReserves.reducer,
  [fromReservesV2.reservesV2FeatureKey]: fromReservesV2.reducer,
  [fromMembership.membershipFeatureKey]: fromMembership.reducer
};

// Note: This sorts the state alphabetically so its easier to debug/troubleshoot in the Redux DevTools viewer!
// https://github.com/zalmoxisus/redux-devtools-extension/issues/416
const appReducer =
  combineReducers(
    Object.fromEntries(
      Object.entries({
        router: fromRouter.routerReducer,
        property: PropertyReducer,
        internal: InternalReducer,
        auth: AuthReducer,
        component: ComponentReducer,
        reports: ReportReducer,
        global: GlobalReducer,
        user: UserReducer,
        plans: PlansReducer,
        workspace: WorkspaceReducer,
        activity: ActivityReducer,
        mapping: MappingReducer,
        setup: SetupReducer,
        locations: LocationsReducer,
        public: PublicReducer,
        filterSettings: FilterSettingsReducer,
        photo: PhotoReducer,
        files: FilesReducer,
        [fromProjects.projectsFeatureKey]: fromProjects.reducer,
        admin: AdminReducer,
        [fromTransactions.transactionFeatureKey]: fromTransactions.reducer,
        [fromConnectedApps.connectedAppsFeatureKey]: fromConnectedApps.reducer,
        [fromCategory.categoryFeatureKey]: fromCategory.reducer,
        [fromTags.tagFeatureKey]: fromTags.reducer,
        [fromPortfolioAnalytics.portfolioAnalyticsFeatureKey]: fromPortfolioAnalytics.reducer,
        [fromMaintenance.maintenanceFeatureKey]: fromMaintenance.reducer,
        [fromWorkspaceGroups.workspaceGroupsFeatureKey]: fromWorkspaceGroups.reducer,
        [fromAssetLibrary.assetLibraryFeatureKey]: fromAssetLibrary.reducer,
        [fromNotifications.notificationsFeatureKey]: fromNotifications.reducer,
        [fromUserSettings.userSettingsFeatureKey]: fromUserSettings.reducer,
        [fromAccounts.accountsFeatureKey]: fromAccounts.reducer,
        [fromReserves.reservesFeatureKey]: fromReserves.reducer,
        [fromReservesV2.reservesV2FeatureKey]: fromReservesV2.reducer,
        [fromMembership.membershipFeatureKey]: fromMembership.reducer
      }).sort()
    )
  );

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function clearState(reducer): any {
  return function (state, action) {

    if (action.type === authActionTypes.LOGOUT || action.type === authActionTypes.UNAUTHORIZED) {
      state = undefined;
    }

    return appReducer(state, action);
  };
}
